<template>
  <div ref="viewer" class="reportViewer" data-bind="dxReportViewer: $data">
  </div>
</template>


<script>
import "devexpress-reporting/dx-webdocumentviewer";
import ko from "knockout";
import { ajaxSetup } from "@devexpress/analytics-core/analytics-utils";
import store from "@/store";
import Axios from "axios";

export default {
  name: "DevXReport",
  props: {
    jobId: Number,
    dataSet: { type: String, default: "ServiceReport" },
    templateName: String,
  },
  components: {},

  data() {
    return {
      origSendRequest: null,
      reportUrlObs: null,   // KO observable for reportUrl
      reportPreview: null,  // DevExpress reportPreview handle
      zoomSub: null,        // KO subscription to zoom observable
      onWheel: null,        // wheel event handler
      initialZoom: null,    // zoom that was applied on init (default or saved)
      userHasZoomed: false, // flips true after first user zoom
      zoomStorageKey: 'servicereortviewer.zoom', // Key for persisted zoom 
    };
  },

  mounted() {
    this.installScopedRequestWrapper(); // scoped override for this component
    this.stampHeaders();                // legacy paths still read this
    this.applyKoBindings();             // bind once
  },

  beforeDestroy() {
    this.clearKo();
    if (this.origSendRequest) {
      ajaxSetup.sendRequest = this.origSendRequest;
      this.origSendRequest = null;
    }
  },

  methods: {
    // ---------- SAFE transport wrapper (keeps jq-style .done/.fail) ----------
    installScopedRequestWrapper() {
      const maybeOriginal = ajaxSetup.sendRequest;
      const $ = window.jQuery || window.$;

      // Fallback transport using jQuery.ajax (returns jqXHR)
      const fallbackSendRequest = (settings) => {
        if (!$ || !$.ajax) {
          const p = Promise.reject(new Error("No transport available"));
          p.done = () => p; p.fail = (cb) => { p.catch(cb); return p; }; p.always = (cb) => { p.finally(cb); return p; };
          return p;
        }
        return $.ajax({
          url: settings.url,
          type: settings.method || "GET",
          data: settings.data,
          headers: settings.headers,
          timeout: settings.timeout,
          xhrFields: { withCredentials: !!settings.withCredentials },
        });
      };

      // Use DevExpress’ original if present, else the fallback
      this.origSendRequest = (typeof maybeOriginal === "function") ? maybeOriginal : fallbackSendRequest;

      const vm = this;

      ajaxSetup.sendRequest = function wrappedSendRequest(settings) {
        const hasJq = $ && $.Deferred;
        const baseSend = (typeof vm.origSendRequest === "function") ? vm.origSendRequest : fallbackSendRequest;

        const addAuth = () => {
          const token = store.state.token?.access_token;
          if (token) settings.headers = { ...(settings.headers || {}), Authorization: `Bearer ${token}` };
        };

        if (hasJq) {
          const dfd = $.Deferred();

          vm.ensureFreshToken()
            .then(() => {
              addAuth();
              const jq = baseSend.call(this, settings);
              jq.done((data) => dfd.resolve(data))
                .fail((err) => dfd.reject(err));
            })
            .catch(() => {
              try {
                addAuth();
                const jq = baseSend.call(this, settings);
                jq.done((data) => dfd.resolve(data))
                  .fail((err) => dfd.reject(err));
              } catch (e) {
                dfd.reject(e);
              }
            });

          return dfd.promise();
        }

        // No jQuery Deferred: return a promise shimmed with .done/.fail
        const p = vm.ensureFreshToken()
          .then(() => { addAuth(); return baseSend.call(this, settings); });

        p.done = (cb) => { p.then(cb); return p; };
        p.fail = (cb) => { p.catch(cb); return p; };
        p.always = (cb) => { p.finally(cb); return p; };
        return p;
      };
    },

    // Keep ajaxSettings updated for legacy codepaths
    stampHeaders() {
      const token = store.state.token?.access_token || "";
      ajaxSetup.ajaxSettings = { headers: { Authorization: `Bearer ${token}` } };
    },

    // ---- Token refresh, scoped to the viewer ----
    async ensureFreshToken() {
      const token = store.state.token?.access_token;
      const expSec = this.getAccessTokenExp(token);
      const now = Math.floor(Date.now() / 1000);
      const skew = 30; // refresh if expiring in <= 30s
      if (expSec && expSec - now > skew) return;
      await this.refreshTokenDirect();
      this.stampHeaders();
    },

    getAccessTokenExp(jwt) {
      try {
        if (!jwt) return null;
        const parts = jwt.split(".");
        if (parts.length !== 3) return null;
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const pad = "=".repeat((4 - (base64.length % 4)) % 4);
        const json = atob(base64 + pad);
        const payload = JSON.parse(json);
        return typeof payload.exp === "number" ? payload.exp : null;
      } catch { return null; }
    },

    async refreshTokenDirect() {
      const refreshToken = store.state.token?.refresh_token;
      if (!refreshToken) throw new Error("No refresh token");

      const refreshClient = Axios.create({
        baseURL: "/",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + refreshToken,
        },
      });

      const res = await refreshClient.post("/api2/token/refresh");
      const data = res.data;
      await store.dispatch("updateToken", data);
      return data;
    },

    // ------------------- KO + Viewer options -------------------
    makeReportUrl(jobId, template, dataSet) {
      const params = new URLSearchParams();

      if (jobId) params.append("id", jobId);
      if (template) params.append("template", template);

      const query = params.toString();
      return query ? `${dataSet}?${query}` : dataSet;
    },

    applyKoBindings() {
      const vm = this;

      // Create the reportUrl observable ONCE; update it on job changes via computed/watch
      this.reportUrlObs = ko.observable(this.makeReportUrl(this.jobId, this.templateName, this.dataSet));

      const hideByDomId = (actions, id) => {
        const a = actions.find((x) => x && x.id === id);
        if (a) a.visible = false;
      };

      const viewerOptions = {
        reportUrl: this.reportUrlObs,
        requestOptions: {
          host: this.isProd ? window.location.origin + "/" : "http://localhost:5000/",
          invokeAction: "ServiceReportViewer",
        },
        callbacks: {
          // ----- INITIALIZE + ZOOM PERSISTENCE + CTRL/CMD WHEEL -----
          BeforeRender(s, e) {
            // (A) initial zoom: default or saved
            const defaultZoom = 0.85; // set your own default; keep constant and predictable
            let savedZoom = NaN;
            try {
              const raw = localStorage.getItem(vm.zoomStorageKey);
              if (raw != null) savedZoom = parseFloat(raw);
            } catch (_) {
              console.warn("ReportViewer: localStorage not available, zoom won't be persisted");
            }
            const initialZoom = Number.isFinite(savedZoom) && savedZoom > 0 ? savedZoom : defaultZoom;

            e.reportPreview.showMultipagePreview(true);
            e.reportPreview.zoom(initialZoom);

            // keep references for later
            vm.reportPreview = e.reportPreview;
            vm.initialZoom = initialZoom;
            vm.userHasZoomed = Number.isFinite(savedZoom); // if we loaded a saved value, treat as "already customized"

            // (B) persist zoom on changes (from wheel or built-in UI)
            if (vm.zoomSub && typeof vm.zoomSub.dispose === "function") {
              vm.zoomSub.dispose();
              vm.zoomSub = null;
            }
            if (e.reportPreview.zoom && e.reportPreview.zoom.subscribe) {
              vm.zoomSub = e.reportPreview.zoom.subscribe((val) => {
                const z = Number(val);
                if (!Number.isFinite(z) || z <= 0) return;

                // Ignore the initial programmatic set so defaults remain until user interacts
                if (!vm.userHasZoomed) {
                  if (Math.abs(z - vm.initialZoom) < 1e-6) return;
                  vm.userHasZoomed = true;
                }

                try { localStorage.setItem(vm.zoomStorageKey, String(z)); } catch (_) {
                  console.warn("ReportViewer: localStorage not available, zoom won't be persisted");
                }
              });
            }

            // (C) Ctrl/Cmd + Wheel to zoom (only inside viewer)
            if (!vm.onWheel && vm.$refs.viewer) {
              vm.onWheel = (evt) => {
                if (!(evt.ctrlKey || evt.metaKey)) return; // require Ctrl (Win/Linux) or Cmd (macOS)
                if (!vm.reportPreview || !vm.reportPreview.zoom) return;

                evt.preventDefault(); // don't let the page scroll/zoom

                const current = Number(vm.reportPreview.zoom());
                if (!Number.isFinite(current) || current <= 0) return;

                const dir = evt.deltaY > 0 ? -1 : 1; // wheel down = out, up = in
                const step = 0.10;
                const minZ = 0.25, maxZ = 3.0;

                let next = current + dir * step;
                next = Math.max(minZ, Math.min(maxZ, next));
                next = Math.round(next * 100) / 100;

                vm.reportPreview.zoom(next);
              };

              // passive:false so preventDefault works
              vm.$refs.viewer.addEventListener("wheel", vm.onWheel, { passive: false });
            }
          },

          CustomizeElements(s, e) {
            const rightPanel = e.GetById("dxrd-right-panel-template-base");
            if (rightPanel) {
              const i = e.Elements.indexOf(rightPanel);
              if (i >= 0) e.Elements.splice(i, 1);
            }
          },

          CustomizeMenuActions(s, e) {
            //console.log("CustomizeMenuActions -> existing actions:", e.Actions);

            hideByDomId(e.Actions, "dxxrp-print-page");
            hideByDomId(e.Actions, "dxxrp-print");
            hideByDomId(e.Actions, "dxxrp-highlight-editing-fields");
            hideByDomId(e.Actions, "dxxrp-next-page");
            hideByDomId(e.Actions, "dxxrp-prev-page");
            hideByDomId(e.Actions, "dxxrp-multipage-toggle");
            hideByDomId(e.Actions, "dxxrp-search");
            hideByDomId(e.Actions, "dxxrp-export-menu");

            /*
            if (!document.getElementById("downloadPdfIcon")) {
              const tpl = document.createElement("script");
              tpl.type = "text/html";
              tpl.id = "downloadPdfIcon";
              tpl.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path class="dxd-icon-fill" d="M5 20h14v-2H5v2zm7-18v10.17l3.59-3.58L17 10l-5 5-5-5 1.41-1.41L11 12.17V2h1z"/>
                </svg>`;
              document.body.appendChild(tpl);
            }

            const downloadAction = {
              id: "DownloadPdfNow",
              text: "Download PDF",
              imageTemplateName: "downloadPdfIcon",
              visible: true,
              disabled: false,
              hasSeparator: false,
              hotKey: { ctrlKey: true, shiftKey: true, keyCode: "D".charCodeAt(0) },
              async clickAction() {
                await vm.ensureFreshToken(); // last-second safety
                s.ExportTo("pdf", false);
              },
            };
            
            e.Actions.push(downloadAction);
            */

            /*
            const printActionDefinition = e.Actions.find((x) => x && x.id === 'dxxrp-print');
            console.log('printActionDefinition', printActionDefinition)
            if (printActionDefinition) {
              printActionDefinition.clickAction = vm.authenticatedPrintAction;
            }
            */

            //console.log("CustomizeMenuActions -> new actions:", e.Actions);
          },

          async OnServerError(s, e) {
            const status =
              e?.Error?.httpResponse?.status ||
              e?.httpResponse?.status ||
              e?.status;

            if (status === 401) {
              try {
                await vm.refreshTokenDirect();
                vm.stampHeaders();
                s.OpenReport(vm.makeReportUrl(vm.jobId, vm.templateName, vm.dataSet));
              } catch {
                store.dispatch("logout");
              }
            }
          },
        },
      };
      //console.log('apply bindings', viewerOptions)
      ko.applyBindings(viewerOptions, this.$refs.viewer);
    },

    clearKo() {
      // dispose zoom subscription
      if (this.zoomSub && typeof this.zoomSub.dispose === "function") {
        this.zoomSub.dispose();
        this.zoomSub = null;
      }
      // remove wheel listener
      if (this.onWheel && this.$refs.viewer) {
        this.$refs.viewer.removeEventListener("wheel", this.onWheel);
        this.onWheel = null;
      }
      this.reportPreview = null;

      if (this.$refs.viewer) ko.cleanNode(this.$refs.viewer);
    },
  },

  computed: {
    isProd() {
      return process.env.NODE_ENV === "production";
    },
  },

  watch: {
    templateName(nv) {
      if (this.reportUrlObs) {
        this.reportUrlObs(this.makeReportUrl(this.jobId, nv, this.dataSet));
      }
    },

    dataSet(nv) {
      if (this.reportUrlObs) {
        this.reportUrlObs(this.makeReportUrl(this.jobId, this.templateName, nv));
      }
    },

    jobId(nv) {
      if (this.reportUrlObs) {
        this.reportUrlObs(this.makeReportUrl(nv, this.templateName, this.dataSet));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.boring:not(:hover)::before {
  background-color: transparent !important;
}
</style>